import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Select from 'react-select';
import SimpleMDE from 'react-simplemde-editor';
import 'easymde/dist/easymde.min.css';
import ReactMarkdown from 'react-markdown';
import ReactDOMServer from 'react-dom/server';
import Modal from '../../Modal';
import { Tabs } from '../../Tabs/Tabs';
import { useDashboard, buildDraftFilterOverrides } from '../contexts/DashboardContext';
import { SQLEditor } from './SQLEditor';
import { ConfigEditor } from './ConfigEditor';
import { DataPreview } from './DataPreview';
import {
  Widget,
  WidgetChartConfig,
  WidgetChartType,
  WidgetDataResult,
  DashboardFilter,
  isBackgroundUpdateIntervalTooShort,
} from '../types';
import { LoaderSmall } from '../../LoaderSmall/LoaderSmall';
import { downloadConfigGuide } from './configGuideContent';
import styles from './WidgetEditor.module.scss';

interface WidgetEditorProps {
  widget: Widget;
  onClose: () => void;
}

const CHART_TYPE_OPTIONS: { value: WidgetChartType; label: string }[] = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'line', label: 'Line Chart' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'table', label: 'Data Table' },
  { value: 'metric', label: 'Single Value Card' },
  { value: 'card_grid', label: 'Card Grid' },
  { value: 'opinions', label: 'Opinions' },
  { value: 'text', label: 'Text' },
  { value: 'map', label: 'Map' },
  { value: 'word_cloud', label: 'Word / Emoji Cloud' },
];

const SIMPLEMDE_OPTIONS = {
  toolbar: [
    'bold', 'italic', 'heading', '|',
    'quote', 'unordered-list', 'ordered-list', '|',
    'link', '|',
    'preview', 'guide',
  ] as const,
  spellChecker: false,
  status: false,
  autofocus: false,
  previewRender: (text: string) =>
    ReactDOMServer.renderToStaticMarkup(<ReactMarkdown>{text}</ReactMarkdown>),
};

export const WidgetEditor: React.FC<WidgetEditorProps> = ({
  widget,
  onClose,
}) => {
  const { t } = useTranslation();
  const { updateWidget, activeDashboard, displayedDashboard, filterValues, saveDraftFilters } = useDashboard();

  // Collect available filters (dashboard-level + widget-level for this widget)
  const availableFilters: DashboardFilter[] = (displayedDashboard?.filters ?? []).filter(
    (f) => !f.widgetId || f.widgetId === widget.id
  );

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [title, setTitle] = useState(widget.title);
  const [chartType, setChartType] = useState<WidgetChartType>(widget.chartType);
  const [sql, setSql] = useState(widget.query.sql);
  const [config, setConfig] = useState<WidgetChartConfig>(widget.config);
  const [previewData, setPreviewData] = useState<WidgetDataResult | null>(
    widget.data
  );
  const [isRunning, setIsRunning] = useState(false);
  const hasLocalChanges = useRef(false);
  const sqlTextareaRef = useRef<HTMLTextAreaElement>(null);

  const insertAtCursor = useCallback((text: string) => {
    const textarea = sqlTextareaRef.current;
    if (!textarea) {
      // Fallback: append to end
      setSql((prev) => prev + text);
      hasLocalChanges.current = true;
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newSql = sql.substring(0, start) + text + sql.substring(end);
    setSql(newSql);
    hasLocalChanges.current = true;
    // Restore cursor position after the inserted text
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
    });
  }, [sql]);

  const isTempWidget = widget.id.startsWith('temp-');

  // Fetch full widget details (including SQL) for saved widgets
  useEffect(() => {
    if (isTempWidget || !activeDashboard?.id) return;
    // Skip fetch if draft already has SQL (previously fetched/edited)
    if (widget.query.sql) return;
    let cancelled = false;
    setIsLoadingDetail(true);
    (async () => {
      try {
        const { DashboardAPI } = await import('../api');
        const detail = await DashboardAPI.getWidget(activeDashboard.id, widget.id);
        if (cancelled) return;
        setTitle(detail.title);
        setChartType(detail.chartType);
        setSql(detail.query.sql);
        setConfig(detail.config);
      } catch {
        // If fetch fails, keep the values we already have
      } finally {
        if (!cancelled) setIsLoadingDetail(false);
      }
    })();
    return () => { cancelled = true; };
  }, [widget.id, isTempWidget, activeDashboard?.id]);

  // Auto-save local state to draft on unmount so edits persist across modal open/close
  const titleRef = useRef(title);
  // Title the widget had when the editor opened — restored by the unmount
  // auto-save if the user cleared the field, since the backend rejects
  // widgets without a title.
  const originalTitleRef = useRef(widget.title);
  const chartTypeRef = useRef(chartType);
  const sqlRef = useRef(sql);
  const configRef = useRef(config);
  const previewDataRef = useRef(previewData);

  useEffect(() => { titleRef.current = title; }, [title]);
  useEffect(() => { chartTypeRef.current = chartType; }, [chartType]);
  useEffect(() => { sqlRef.current = sql; }, [sql]);
  useEffect(() => { configRef.current = config; }, [config]);
  useEffect(() => { previewDataRef.current = previewData; }, [previewData]);

  useEffect(() => {
    return () => {
      if (hasLocalChanges.current) {
        updateWidget(widget.id, {
          title: titleRef.current.trim() ? titleRef.current : originalTitleRef.current,
          chartType: chartTypeRef.current,
          query: { sql: chartTypeRef.current === 'text' ? '--' : sqlRef.current },
          config: configRef.current,
          data: previewDataRef.current,
        });
      }
    };
  }, [widget.id, updateWidget]);

  const handleRunQuery = useCallback(async () => {
    if (!sql.trim()) {
      toast.warning(t('Enter a SQL query first') as string);
      return;
    }
    if (!activeDashboard?.id) return;
    setIsRunning(true);
    try {
      // Auto-save any unsaved draft filters so the backend knows about them
      await saveDraftFilters();

      const { DashboardAPI } = await import('../api');

      // Collect current filter values (non-null only) for backend resolution.
      // Include hidden filters' draft default values so unsaved changes apply
      // to the preview (backend honours these when include_hidden_values=true).
      const currentFilterValues: Record<string, any> = {};
      Object.entries(filterValues).forEach(([k, v]) => {
        if (v != null) currentFilterValues[k] = v;
      });
      const draftFilters = displayedDashboard?.filters ?? [];
      draftFilters.forEach((f) => {
        if (!f.hidden) return;
        if (currentFilterValues[f.parameterName] !== undefined) return;
        if (f.defaultValue != null) currentFilterValues[f.parameterName] = f.defaultValue;
      });

      // Send draft filter overrides so the backend uses unsaved filter SQL when
      // resolving cascading values / __all__ expansion of hidden allow_all filters.
      // Filters whose SQL was never lazy-loaded (empty querySql) are skipped so we
      // don't overwrite the backend's real stored SQL with an empty string — that
      // would make the filter resolve to nothing and not apply. See
      // buildDraftFilterOverrides for details.
      const draftOverrides = buildDraftFilterOverrides(draftFilters);

      const data = await DashboardAPI.executeRawQuery(
        activeDashboard.id, sql, currentFilterValues, undefined, undefined, true,
        draftOverrides,
      );
      setPreviewData(data);
      if (data.error) {
        toast.error(t('Query returned an error') as string);
      }
    } catch (err: any) {
      const errorMsg = err?.message || 'Query execution failed';
      setPreviewData({
        columns: [],
        rows: [],
        error: errorMsg,
      });
      toast.error(t('Query execution failed') as string);
    } finally {
      setIsRunning(false);
    }
  }, [sql, activeDashboard?.id, t, filterValues, saveDraftFilters, displayedDashboard?.filters]);

  const handleSave = async () => {
    // The dashboard save (PATCH/POST widget) fails server-side on an empty
    // title, so block it here instead of reporting a success that can't persist.
    if (!title.trim()) {
      toast.warning(t('Widget title is required') as string);
      return;
    }
    if (isBackgroundUpdateIntervalTooShort(config)) {
      toast.warning(t('background_update refresh interval must be at least 3600 seconds') as string);
      return;
    }
    updateWidget(widget.id, {
      title: title.trim(),
      chartType,
      query: { sql: chartType === 'text' ? '--' : sql },
      config,
      data: previewData,
    });

    hasLocalChanges.current = false; // Prevent duplicate save on unmount
    toast.success(t('Widget updated') as string);
    onClose();
  };

  const sqlHintsByType: Record<WidgetChartType, { description: string; example: string }> = {
    bar: {
      description: t('Return columns for X-axis categories and Y-axis values. Add a grouping column to create multi-series bars.'),
      example: 'SELECT category, value FROM ...\n-- or with grouping:\nSELECT category, series_name, value FROM ...',
    },
    line: {
      description: t('Return columns for X-axis points and Y-axis values. Add a grouping column for multiple lines.'),
      example: 'SELECT date, value FROM ...\n-- or with grouping:\nSELECT date, series_name, value FROM ...',
    },
    pie: {
      description: t('Return two columns: slice name and slice value.'),
      example: 'SELECT name, value FROM ...',
    },
    table: {
      description: t('Return any columns to display as a table. For server-side pagination, add LIMIT/OFFSET using built-in variables and set "pageSize" in Configuration.'),
      example: 'SELECT col1, col2 FROM ...\n-- with pagination:\nSELECT col1, col2 FROM ...\nLIMIT {{__limit__}} OFFSET {{__offset__}}',
    },
    metric: {
      description: t('Return a time series (date + value columns) for a sparkline and auto-computed trend, or a single row for a plain value card. Use {{trend_days}} as a parameter and sync it with metricConfig.trendRange.'),
      example: "SELECT DATE(created_at) AS date, COUNT(*) AS value\nFROM ...",
    },
    card_grid: {
      description: t('Return one row per card. Configure dateCol, titleCol, descriptionCol, sparklineCol (JSON array string), and metric columns via cardGridConfig.'),
      example: "SELECT TO_CHAR(created_at, 'DD/MM/YYYY') AS date, title, description,\n  NULL AS sparkline_data, publications, views\nFROM ...",
    },
    opinions: {
      description: t('Return one row per opinion card. Configure titleCol, sentimentCol ("positive"/"neutral"/"negative"), and metric columns (e.g. rating, publications, actors) via opinionsConfig.'),
      example: "SELECT title, sentiment, rating, publications, actors\nFROM ...",
    },
    text: { description: '', example: '' },
    map: {
      description: t('Return a code column and numeric columns for tooltip and choropleth coloring. World map: ISO-2 country codes (e.g. "US", "DE"). Regional maps (mapType "ukraine"/"germany"/"russia"): ISO 3166-2 region codes (e.g. "UA-63", "DE-BY", "RU-MOW"), or RU-CFO-style district codes for the Russia map with federalLevel "districts".'),
      example: "SELECT c.code AS country_code,\n  SUM(na.messages_count) AS publications,\n  COUNT(DISTINCT na.actor_id) AS actors\nFROM narrative_actors na\nJOIN source s ON s.id = na.actor_id\nJOIN country c ON c.id = s.origin_country_id\nWHERE na.narrative_id = ANY({{case_ids}})\nGROUP BY c.code\nORDER BY publications DESC\n-- or regional (mapType \"ukraine\"/\"germany\"/\"russia\"), group IDs translated to region codes via regionMapping in config:\nSELECT sg.group_id::text AS group_id,\n  COUNT(DISTINCT sg.source_id) AS actors,\n  COALESCE(SUM(psm.last_7d_engagement), 0)::bigint AS engagement_7d\nFROM source_group sg\nLEFT JOIN precalculated_source_metrics psm ON psm.source_id = sg.source_id\nWHERE sg.group_id IN (101, 102, 103)\n  AND sg.workspace_id = {{ws}}::int -- {{ws}} = a custom hidden filter for workspace isolation (see guide)\nGROUP BY sg.group_id\nORDER BY engagement_7d DESC",
    },
    word_cloud: {
      description: t('Return a token column (keyword or emoji) and a numeric frequency column, one row per token. Optionally add a third column with "keyword" / "emoji" and point wordCloudConfig.kindColumn at it; otherwise emojis are detected automatically. Optional extras: a relative-change column for trend coloring (wordCloudConfig.trendColumn) and a sample-size column for the caption (wordCloudConfig.sampleColumn).'),
      example: "SELECT word AS name, COUNT(*) AS value\nFROM ...\nGROUP BY word\nORDER BY value DESC\nLIMIT 150\n-- with trend and sample size:\nSELECT word AS name, COUNT(*) AS value, 0.4 AS trend, 1000 AS sample_size\nFROM ...",
    },
  };

  const currentHint = sqlHintsByType[chartType];

  const sqlHints = (
    <div className={styles.sqlHints}>
      <div className={styles.sqlHintBlock}>
        <span className={styles.sqlHintLabel}>{currentHint.description}</span>
        <pre className={styles.sqlHintExample}>{currentHint.example}</pre>
      </div>
      {availableFilters.length > 0 && (
        <div className={styles.filterHints}>
          <span className={styles.filterHintsLabel}>{t('Available filters')}:</span>
          {availableFilters.flatMap((f) => {
            if (f.filterType === 'date_range') {
              const fromP = `{{${f.parameterName}_from}}`;
              const toP = `{{${f.parameterName}_to}}`;
              return [
                <code key={`${f.id}_from`} className={styles.filterHintTag} title={`${f.title} from — click to insert`} onClick={() => insertAtCursor(fromP)}>{fromP}</code>,
                <code key={`${f.id}_to`} className={styles.filterHintTag} title={`${f.title} to — click to insert`} onClick={() => insertAtCursor(toP)}>{toP}</code>,
              ];
            }
            const placeholder = `{{${f.parameterName}}}`;
            return [(
              <code key={f.id} className={styles.filterHintTag} title={`${f.title} (${f.filterType}${f.widgetId ? ', widget-level' : ''}) — click to insert`} onClick={() => insertAtCursor(placeholder)}>{placeholder}</code>
            )];
          })}
        </div>
      )}
    </div>
  );

  const handleTextContentChange = useCallback((value: string) => {
    setConfig((prev) => ({
      ...prev,
      textConfig: { ...prev.textConfig, content: value },
    }));
    hasLocalChanges.current = true;
  }, []);

  const tabslist = chartType === 'text'
    ? [
        {
          title: t('Content'),
          content: (
            <SimpleMDE
              value={config.textConfig?.content ?? ''}
              onChange={handleTextContentChange}
              options={SIMPLEMDE_OPTIONS}
            />
          ),
        },
        {
          title: t('Configuration'),
          content: <ConfigEditor config={config} onChange={(val: WidgetChartConfig) => { setConfig(val); hasLocalChanges.current = true; }} chartType={chartType} />,
        },
      ]
    : [
        {
          title: t('SQL'),
          content: (
            <>
              <SQLEditor
                ref={sqlTextareaRef}
                value={sql}
                onChange={(val: string) => { setSql(val); hasLocalChanges.current = true; }}
                onRun={handleRunQuery}
                isRunning={isRunning}
                executionTimeMs={previewData?.executionTimeMs}
              />
              {sqlHints}
            </>
          ),
        },
        {
          title: t('Configuration'),
          content: <ConfigEditor config={config} onChange={(val: WidgetChartConfig) => { setConfig(val); hasLocalChanges.current = true; }} chartType={chartType} />,
        },
        {
          title: t('Preview'),
          content: <DataPreview data={previewData} chartType={chartType} config={config} querySql={sql} />,
        },
      ];

  const footer = (
    <>
      <button className="new-button" onClick={handleSave}>
        {t('Save')}
      </button>
      <button className="btn-reset" onClick={onClose}>
        {t('Cancel')}
      </button>
      <button className={styles.guideBtn} onClick={downloadConfigGuide} title={t('Download configuration guide')}>
        &#8681; {t('Config Guide')}
      </button>
    </>
  );

  return (
    <Modal
      isVisible={true}
      title={t('Edit Widget')}
      onClose={onClose}
      className={styles.modal}
      content={
        isLoadingDetail ? (
          <div className={styles.editorContent}>
            <LoaderSmall />
          </div>
        ) : (
        <div className={styles.editorContent}>
          <div className={styles.topFields}>
            <div className="form-element">
              <label>{t('Title')}</label>
              <input
                type="text"
                value={title}
                onChange={(e) => { setTitle(e.target.value); hasLocalChanges.current = true; }}
              />
            </div>
            <div className="form-element">
              <label>{t('Chart Type')}</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={CHART_TYPE_OPTIONS}
                value={CHART_TYPE_OPTIONS.find((o) => o.value === chartType)}
                onChange={(option) => {
                  if (option) { setChartType(option.value); hasLocalChanges.current = true; }
                }}
                isSearchable={false}
                isClearable={false}
              />
            </div>
          </div>
          <Tabs tabslist={tabslist} heading="" switchMode={[]} activeMode={0} />
        </div>
        )
      }
      footer={footer}
    />
  );
};
